import { useRef, useState } from 'react';
import { startVoiceTake, voiceSupported, type VoiceTake } from '../net/voice';
import { DEFAULT_SETTINGS } from '../../shared/constants';

/** v11 §2D — the walkie-talkie mic: ONE button, hold to record (≤10 s),
 *  release to send. No extra chrome; the recording state is the button. */
export function VoiceMic({ onSend, onFail }: { onSend: (dataUrl: string) => void; onFail?: () => void }) {
  const take = useRef<VoiceTake | null>(null);
  const [recording, setRecording] = useState(false);
  if (!voiceSupported()) return null;

  const start = async () => {
    if (take.current) return;
    const t = await startVoiceTake(DEFAULT_SETTINGS.voiceMaxSeconds * 1000);
    if (!t) return;
    take.current = t;
    setRecording(true);
  };
  const finish = async () => {
    const t = take.current;
    take.current = null;
    setRecording(false);
    if (!t) return;
    const dataUrl = await t.stop();
    // v17 BOLT-077 §7: a take that produced nothing used to fail SILENTLY —
    // the player pressed, spoke, and never learned it went nowhere.
    if (dataUrl) onSend(dataUrl);
    else onFail?.();
  };
  const abort = () => {
    take.current?.cancel();
    take.current = null;
    setRecording(false);
  };

  return (
    <button
      className={`voice-mic${recording ? ' voice-mic--rec' : ''}`}
      data-testid="voice-mic"
      data-recording={recording}
      aria-label="Hold to talk"
      onPointerDown={(e) => {
        e.preventDefault();
        void start();
      }}
      onPointerUp={() => void finish()}
      onPointerLeave={() => {
        if (recording) void finish(); // slid off — send what we have
      }}
      onPointerCancel={abort}
      onContextMenu={(e) => e.preventDefault()}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {recording && <span className="voice-mic__dot" />}
    </button>
  );
}
